import { Button, Loading, Message, Title } from "@components";

import styles from "styles/pages/reports/reports.module.scss";
import ContemplationXUses from "./ContemplationXUses";
import UsageBehavior from "./UsageBehavior";
import UsesAndExpiration from "./UsesAndExpiration";
import CouponsInPeriod from "./CouponsInPeriod";
import UsageRecord from "pages/Reports/Coupon/UsageRecord";
import { Currency } from "shared/utils/Currency";
import { If } from "shared/components/control-statements";
import ReportFilters from "pages/Reports/ReportFilters";
import { useEffect, useRef, useState } from "react";
import Filters from "pages/Reports/Filters";
import { ReportsAPI } from "providers/ReportsAPI";
import { Numbers } from "shared/utils/Numbers";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import DistribuitionUses from "./DistribuitionUses";
import dayjs from "dayjs";
import AcquisitionRecord from "pages/Reports/Coupon/AcquisitionRecord";
import AquisitionBehavior from "./AquisitionBehavior";
import { formatNumberWithOneDecimal, Trend } from "pages/Reports/Reports";
import OperationsRecord from "../LoyaltyOperations/OperationsRecord";
import OperationsRanking from "../LoyaltyOperations/OperationsRanking";
import { send } from "process";
import IndicatorsDetails from "../LoyaltyIndicators/IndicatorsDetails";

const numbers = (data = {}, isBeforeFebruary, isBeforeNovember) => {
  return [
    {
      title: "Faturamento bruto",
      value: isBeforeNovember
        ? "---"
        : Currency.formatCurrency(data.total?.value ?? data.total),
      tooltip:
        "Soma do valor total bruto de todos os pedidos utilizando cupons.",
      growth: isBeforeNovember ? null : data.total?.growth || 0,
    },
    {
      alias: "discounts_cost",
      title: "Descontos totais",
      value: isBeforeFebruary
        ? "---"
        : Currency.formatCurrency(data.discounts?.value ?? data.discounts),
      tooltip:
        "Soma do valor total de descontos aplicados nos pedidos que utilizaram cupons.",
      growth: isBeforeFebruary ? null : data.discounts?.growth || 0,
    },
    {
      title: "Faturamento líquido",
      value:
        isBeforeFebruary || isBeforeNovember
          ? "---"
          : Currency.formatCurrency(
              data.total_with_discounts?.value ?? data.total_with_discounts
            ),
      tooltip:
        "Soma do valor total líquido de todos os pedidos utilizando cupons.",
      growth: isBeforeNovember ? null : data.total_with_discounts?.growth,
    },
  ];
};

const numbers2 = (data = {}, hasUnitsReportFilters) => {
  return [
    {
      alias: "use_ratio_1",
      title: "Taxa de conversão (Pontos x Aquisições)",
      value: hasUnitsReportFilters
        ? "---"
        : Numbers.formatPercent(data.use_ratio_1?.value),
      tooltip: "",
      growth: hasUnitsReportFilters ? null : data.use_ratio_1?.growth,
    },
    {
      alias: "expired_ratio_1",
      title: "Taxa de expiração (Pontos x Expiração)",
      value: hasUnitsReportFilters
        ? "---"
        : Numbers.formatPercent(data.expired_ratio_1?.value),
      tooltip: "",
      growth: hasUnitsReportFilters ? null : data.expired_ratio_1?.growth,
    },
    {
      alias: "use_ratio",
      title: "Taxa de conversão (Aquisições x Usos)",
      value: hasUnitsReportFilters
        ? "---"
        : Numbers.formatPercent(data.use_ratio?.value),
      tooltip: "",
      growth: hasUnitsReportFilters ? null : data.use_ratio?.growth,
    },
    {
      alias: "expired_ratio",
      title: "Taxa de expiração (Aquisições x Expiração)",
      value: hasUnitsReportFilters
        ? "---"
        : Numbers.formatPercent(data.expired_ratio?.value),
      tooltip: "",
      growth: hasUnitsReportFilters ? null : data.expired_ratio?.growth,
    },
  ];
};

function calculateTotalROI(data) {
  if (!data || typeof data !== "object") return 0;

  const total_revenue = Number(data.total) || 0;
  const coupon_discounts = Number(data.discounts) || 0;
  const cost = Number(data.send_cost) || 0;

  const investment = coupon_discounts + cost;
  if (investment === 0) return 0;

  const roi = (total_revenue - investment) / investment;
  return roi;
}

const numbers3 = (data = {}, isBeforeFebruary, isBeforeNovember) => {
  const roi = calculateTotalROI(data);

  return [
    {
      title: "Custos com disparos",
      alias: "total_cost",
      value: Currency.formatCurrency(data.send_cost?.value),
      growth: data.send_cost?.growth,
    },
    {
      title: "Ticket médio bruto",
      value: isBeforeNovember
        ? "---"
        : Currency.formatCurrency(data.average_ticket?.value),
      tooltip:
        "Soma do valor total bruto dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
      growth: isBeforeNovember ? null : data.average_ticket?.growth,
    },
    {
      title: "Ticket médio líquido",
      value:
        isBeforeFebruary || isBeforeNovember
          ? "---"
          : Currency.formatCurrency(data.average_ticket_with_discounts?.value),
      tooltip:
        "Soma do valor total líquido dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
      growth: isBeforeNovember
        ? null
        : data.average_ticket_with_discounts?.growth,
    },
    {
      title: "ROI total",
      value:
        isBeforeFebruary || isBeforeNovember
          ? "---"
          : formatNumberWithOneDecimal(roi),
      tooltip:
        "Retorno sobre o investimento do cupom. FÓRMULA: {{ ((Faturamento gerado - Descontos totais) / Descontos totais) * 100 }}.",
      growth: isBeforeNovember ? null : data.roi?.growth,
    },
  ];
};

const numbers4 = (data = {}, hasUnitsReportFilters) => {
  return [
    {
      title: "Pontos gastos com aquisições",
      value: hasUnitsReportFilters
        ? "---"
        : `${Numbers.formatFloat(data.points?.value)}`,
      growth: data.points?.growth,
    },
    {
      title: "Quantidade de aquisições",
      value: hasUnitsReportFilters
        ? "---"
        : Numbers.formatInt(data.added?.value),
      growth: data.added?.growth,
    },
    {
      title: "Quantidade de usos",
      value: Numbers.formatFloat(data.uses?.value),
      growth: data.uses?.growth,
    },
    {
      alias: "time_to_use",
      title: "Tempo médio para uso",
      value: `${Numbers.formatFloat(data.time_to_use?.value)} dias`,
      tooltip: "Média do tempo entre as adições e os usos deste cupom.",
      growth: data.time_to_use?.growth,
    },
  ];
};

const numbers5 = (data = {}, isCashback, hasUnitsReportFilters) => {
  return [
    {
      title: isCashback ? "Cashback concedido" : "Pontos concedidos",
      count: {
        value: data?.received?.count?.value,
        growth: data?.received?.count?.growth,
      },
      total: {
        value: data?.received?.total?.value,
        growth: data?.received?.total?.growth,
      },
    },
    {
      title: isCashback ? "Cashback consumido" : "Pontos consumidos",
      count: {
        value: hasUnitsReportFilters ? "---" : data?.used?.count?.value,
        growth: hasUnitsReportFilters ? null : data?.used?.count?.growth,
      },
      total: {
        value: hasUnitsReportFilters ? "---" : data?.used?.total?.value,
        growth: hasUnitsReportFilters ? null : data?.used?.total?.growth,
      },
    },
    {
      alias: "expired",
      title: isCashback ? "Cashback expirado" : "Pontos expirados",
      count: {
        value: data?.expired?.count?.value,
        growth: data?.expired?.count?.growth,
      },
      total: {
        value: data?.expired?.total?.value,
        growth: data?.expired?.total?.growth,
      },
    },
    {
      alias: "chargeback",
      title: isCashback ? "Cashback estornado" : "Pontos estornados",
      count: {
        value: data?.chargeback?.count?.value,
        growth: data?.chargeback?.count?.growth,
      },
      total: {
        value: data?.chargeback?.total?.value,
        growth: data?.chargeback?.total?.growth,
      },
    },
  ];
};

const BigNumber = ({ data, hasReportFilters }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value}</p>
          <If cond={!hasReportFilters}>
            <Trend
              growth={data.growth}
              invertColorSign={[
                "expired_ratio_1",
                "expired_ratio",
                "time_to_use",
                "total_cost",
                "send_cost",
                "expired",
                "chargeback",
                "cost",
                "discounts_cost",
              ].includes(data.alias)}
              tooltip="Comparado com o mesmo periodo do mês anterior"
            />
          </If>
        </div>
        <div className={styles.title}>
          <p>{data.title}</p>
          <If cond={data.tooltip}>
            <span className={styles.info} title={data.tooltip}>
              <i className="fal fa-info-circle" />
            </span>
          </If>
        </div>
      </div>
    </div>
  );
};

const BigNumber2 = ({ data, isCashback, hasReportFilters }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column w-100">
        <div className={styles.value}>
          <p>
            <If cond={data.total.value !== "---"}>
              {isCashback
                ? Currency.formatCurrency(+data.total.value)
                : Numbers.formatInt(+data.total.value)}
            </If>
            <If cond={data.total.value === "---"}>---</If>
          </p>
          <If cond={!hasReportFilters}>
            <Trend
              growth={+data?.total.growth}
              invertColorSign={[
                "expired_ratio",
                "time_to_use",
                "total_cost",
                "send_cost",
                "expired",
                "chargeback",
                "cost",
                "discounts_cost",
                "total_cost",
                "expired",
                "chargeback",
              ].includes(data.alias)}
              tooltip="Comparado com o mesmo periodo do mês anterior"
            />
          </If>
        </div>
        <div className={styles.value}>
          <p>
            <If cond={data.total.value !== "---"}>
              {Numbers.formatInt(+data.count.value)}
            </If>
            <If cond={data.total.value === "---"}>---</If>

            <span>(OPS)</span>
          </p>
          <If cond={!hasReportFilters}>
            <Trend
              growth={+data?.count.growth}
              invertColorSign={[
                "expired_ratio",
                "time_to_use",
                "total_cost",
                "send_cost",
                "expired",
                "chargeback",
                "cost",
                "discounts_cost",
                "total_cost",
                "expired",
                "chargeback",
              ].includes(data.alias)}
              tooltip="Comparado com o mesmo periodo do mês anterior"
            />
          </If>
        </div>

        <div className={styles.title}>
          <p>{data.title}</p>
        </div>
      </div>
    </div>
  );
};

const BigNumberBalance = ({ data = {}, hasUnitsReportFilters }) => {
  const program = localStorage.getItem("cashbackProgram")
    ? JSON.parse(localStorage.getItem("cashbackProgram"))
    : null;

  const isCashback = program?.type !== "POINT";

  return (
    <div className={styles.bigNumberOutlined}>
      <div className="d-flex flex-column w-100">
        <div className="d-flex align-center justify-between">
          <div className="d-flex align-center gap-10">
            <i
              className="far fa-hand-holding-dollar"
              style={{ color: "#6F767E" }}
            />
            <p className={styles.number}>
              <If cond={hasUnitsReportFilters}>---</If>
              <If cond={!hasUnitsReportFilters}>
                {isCashback
                  ? Currency.formatCurrency(data.total_available?.value)
                  : Numbers.formatInt(data.total_available?.value)}
              </If>
            </p>
          </div>
        </div>
        <div className={styles.title}>
          <p>{isCashback ? "Cashback" : "Pontos"} em saldo</p>
          <span
            className={styles.info}
            title="Total de cashback disponível para uso na carteira de clientes."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default function Index() {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [couponUses, setCouponUses] = useState([]);
  const [aquisitions, setAquisitions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPage2, setCurrentPage2] = useState(1);
  // const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = (couponUses || [])?.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil((couponUses || []).length / itemsPerPage);

  const indexOfLastItemAquisition = currentPage2 * itemsPerPage;
  const indexOfFirstItemAquisition = indexOfLastItemAquisition - itemsPerPage;
  const currentItemsAquisition = (aquisitions || [])?.slice(
    indexOfFirstItemAquisition,
    indexOfLastItemAquisition
  );

  const totalPagesAquisition = Math.ceil(
    (aquisitions || []).length / itemsPerPage
  );

  const filtersRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    behavior_added: [],
    behavior_use: [],
    distribution: {},
    expiration_uses: [],
    gain_uses: {},
    period: [],
    statistics: {},
  });
  const [operationsData, setOperationsData] = useState({});
  const [indicatorsData, setIndicatorsData] = useState({});
  const [label_dict, setLabelDict] = useState(new Map());

  const provider = new ReportsAPI();

  const fetchData = async () => {
    const response = await provider.loyaltyCoupons(null, {
      ...filters,
      ...(filters.period === "FROM_THE_BEGINNING" && {
        start_date: null,
        end_date: null,
      }),
      ...(!filters.period && {
        start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
        end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
      }),
    });

    setData(response);
  };

  const fetchCouponUses = async (page = 1, page_size = 10) => {
    const response = await provider.loyaltyCoupons(
      "uses",
      {
        ...filters,
        ...(filters.period === "FROM_THE_BEGINNING" && {
          start_date: null,
          end_date: null,
        }),
        ...(!filters.period && {
          start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
          end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
        }),
      },
      {
        page,
        page_size,
      }
    );

    setCouponUses(response.data);
  };

  const fetchAquisitions = async (page = 1, page_size = 10) => {
    const response = await provider.loyaltyCoupons(
      "adds",
      {
        ...filters,
        ...(filters.period === "FROM_THE_BEGINNING" && {
          start_date: null,
          end_date: null,
        }),
        ...(!filters.period && {
          start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
          end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
        }),
      },
      {
        page,
        page_size,
      }
    );

    setAquisitions(response.data);
  };

  const fetchOperationsData = async () => {
    const response = await provider.loyaltyOperations(null, filters);
    setOperationsData(response);
  };

  const fetchIndicators = async () => {
    const response = await provider.loyaltyIndicators(filters);
    setIndicatorsData(response);
  };

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await Promise.all([
        fetchData(),
        fetchIndicators(),
        fetchOperationsData(),
        fetchCouponUses(1, 3000),
        fetchAquisitions(1, 3000),
        fetchUnits(),
      ]);
      setLoading(false);
    };

    fetchDataAsync();
  }, [filters]);

  const fetchUnits = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      // fetchCouponUses(page);
      setCurrentPage(page);
    }
  };

  const handlePageChange2 = (page) => {
    if (page >= 1 && page <= totalPages) {
      // fetchCouponUses(page);
      setCurrentPage2(page);
    }
  };

  const february2025 = dayjs("2025-02-01T00:00:00.000Z");
  const currentBeforeFebruary = dayjs(filters?.start_date || "").isBefore(
    february2025
  );

  const isBeforeFebruary =
    currentBeforeFebruary || filters?.period === "FROM_THE_BEGINNING";

  const november2025 = dayjs("2025-11-01T00:00:00.000Z");
  const october2025 = dayjs("2025-10-24T00:00:00.000Z");

  const currentBeforeNovember = dayjs(filters?.start_date || "").isBefore(
    november2025
  );
  const currentBeforeOctober = dayjs(filters?.start_date || "").isBefore(
    october2025
  );

  const isBeforeNovember =
    currentBeforeNovember ||
    filters?.period === "FROM_THE_BEGINNING" ||
    dayjs().isBefore(november2025);

  const isBeforeOctober =
    currentBeforeOctober ||
    filters?.period === "FROM_THE_BEGINNING" ||
    dayjs().isBefore(october2025);

  const hasReportFilters =
    filters?.period ||
    filters.unit_id?.length > 0 ||
    filters.origin?.length > 0;

  const specificDay = filters?.period === "SPECIFIC_DAY";

  const program = localStorage.getItem("cashbackProgram")
    ? JSON.parse(localStorage.getItem("cashbackProgram"))
    : null;

  const isCashback = program?.type !== "POINT";

  const mergedPeriods = data?.period?.map((item1) => {
    const match = operationsData?.period?.find(
      (item2) => item2.date === item1.date
    );

    return { ...item1, ...match };
  });

  const periodsMerged = (mergedPeriods || []).map((item1) => {
    const match = indicatorsData?.periods?.find(
      (item2) => item2.date === item1.date
    );

    if (!match) return item1;

    return {
      ...item1,
      send_cost: {
        value: match.send_cost?.total,
        growth: match.send_cost?.growth,
      },
      use_ratio_1: {
        value: match.use_ratio?.total,
        growth: match.use_ratio?.growth,
      },
      expired_ratio_1: {
        value: match.expired_ratio?.total,
        growth: match.expired_ratio?.growth,
      },
    };
  });

  const deepMergeArraysByKey = (obj1 = {}, obj2 = {}) => {
    const result = {};

    const idMap = {
      units: "unit_id",
      origins: "origin",
      brands: "brand_id",
      coupons: "date",
    };

    const allKeys = new Set([
      ...Object.keys(obj1 || {}),
      ...Object.keys(obj2 || {}),
    ]);

    for (const key of allKeys) {
      const arr1 = Array.isArray(obj1[key])
        ? obj1[key]
        : obj1[key]
        ? [obj1[key]]
        : [];
      const arr2 = Array.isArray(obj2[key])
        ? obj2[key]
        : obj2[key]
        ? [obj2[key]]
        : [];

      if (key === "coupons") {
        result[key] = arr2.length ? arr2 : arr1;
        continue;
      }

      const idKey = idMap[key] || "date";
      const merged = [];
      const usedIds = new Set();

      for (const item1 of arr1) {
        const id = item1[idKey];
        if (id == null) continue;

        const match = arr2.find((item2) => item2[idKey] === id);
        if (match) {
          merged.push({ ...item1, ...match });
          usedIds.add(id);
        } else {
          merged.push(item1);
          usedIds.add(id);
        }
      }

      const extras = arr2.filter(
        (item2) => item2[idKey] != null && !usedIds.has(item2[idKey])
      );

      result[key] = [...merged, ...extras];
    }

    return result;
  };

  const detailsWithRenamedFields = Object.fromEntries(
    Object.entries(indicatorsData.details || {}).map(([key, arr]) => [
      key,
      arr.map((item) => ({
        unit_id: item.unit_id,
        expired_ratio_points: item.expired_ratio,
        use_ratio_points: item.use_ratio,
        send_cost: item.send_cost,
      })),
    ])
  );

  const distributionMerged = deepMergeArraysByKey(
    data?.distribution || {},
    detailsWithRenamedFields || {}
  );

  const finalDistribution = deepMergeArraysByKey(
    distributionMerged || {},
    operationsData?.distribution
  );

  // console.log(data?.distribution);
  // console.log(operationsData?.distribution);
  // console.log("finalDistribution", finalDistribution);

  const mergedStatistics = {
    ...data.statistics,
    ...{
      send_cost: {
        value: indicatorsData?.statistics?.send_cost?.value,
        growth: indicatorsData?.statistics?.send_cost?.growth,
      },
      use_ratio_1: {
        value: indicatorsData?.statistics?.use_ratio?.value,
        growth: indicatorsData?.statistics?.use_ratio?.growth,
      },
      expired_ratio_1: {
        value: indicatorsData?.statistics?.expired_ratio?.value,
        growth: indicatorsData?.statistics?.expired_ratio?.growth,
      },
    },
  };

  const hasUnitsReportFilters =
    filters.coupons?.length > 0 ||
    filters.unit_id?.length > 0 ||
    filters.origin?.length > 0;

  return (
    <>
      <Title
        title="Indicadores de pontos"
        backTo={{ url: "/relatorios/fidelizacao", name: "Fidelização" }}
      >
        <Filters
          onRemoveFilter={(filter) => {
            filtersRef.current?.removeFilter(filter);
          }}
          filters={filters}
          units={label_dict}
        />
        <Button onClick={() => setOpenFilters(true)}>Filtrar</Button>
      </Title>

      <If cond={isBeforeOctober}>
        <Message type="warning" className="mb-15">
          Alguns dados de origens não Olga anteriores a 24/10 podem não ser
          exibidos.
        </Message>
      </If>

      <div className={styles.bigNumbersIdentification}>
        {numbers(data.statistics || {}, isBeforeFebruary, isBeforeNovember).map(
          (item) => (
            <BigNumber
              key={item.title}
              data={item}
              hasReportFilters={hasReportFilters}
            />
          )
        )}
      </div>
      <div className={styles.bigNumbersIdentification4}>
        {numbers3(
          mergedStatistics || {},
          isBeforeFebruary,
          isBeforeNovember
        ).map((item) => (
          <BigNumber
            key={item.title}
            data={item}
            hasReportFilters={hasReportFilters}
          />
        ))}
      </div>
      <div className={styles.bigNumbersIdentification4}>
        {numbers2(mergedStatistics || {}, hasUnitsReportFilters).map((item) => (
          <BigNumber
            key={item.title}
            data={item}
            hasReportFilters={hasReportFilters}
          />
        ))}
      </div>

      <div className={styles.bigNumbersIdentification4}>
        {numbers4(data.statistics || {}, hasUnitsReportFilters).map((item) => (
          <BigNumber
            key={item.title}
            data={item}
            hasReportFilters={hasReportFilters}
          />
        ))}
      </div>
      <div className={styles.bigNumbersOperations}>
        {numbers5(
          operationsData.statistics,
          isCashback,
          hasUnitsReportFilters
        ).map((item) => (
          <BigNumber2
            key={item.title}
            data={item}
            isCashback={isCashback}
            hasReportFilters={hasReportFilters}
          />
        ))}
        <BigNumberBalance
          data={indicatorsData?.statistics}
          hasUnitsReportFilters={hasUnitsReportFilters}
        />
      </div>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={!specificDay}>
        <CouponsInPeriod
          data={periodsMerged || []}
          isBeforeNovember={isBeforeNovember}
          hasUnitsReportFilters={hasUnitsReportFilters}
        />
      </If>
      <DistribuitionUses
        data={finalDistribution}
        isBeforeNovember={isBeforeNovember}
        isBeforeFebruary={isBeforeFebruary}
        filters={filters}
        isCashback={isCashback}
        hasUnitsReportFilters={hasUnitsReportFilters}
      />
      <ContemplationXUses data={data.gain_uses} />
      <UsageBehavior data={data.behavior_use} />
      <AquisitionBehavior data={data.behavior_added} />
      {/* <UsesAndExpiration data={data?.expiration_uses} /> */}
      <UsageRecord
        data={currentItems}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalPages={totalPages}
      />

      {/* <AcquisitionRecord
        data={currentItemsAquisition}
        handlePageChange={handlePageChange2}
        currentPage={currentPage2}
        totalPages={totalPagesAquisition}
      /> */}

      <OperationsRecord isCashback={isCashback} filters={filters} />
      <OperationsRanking isCashback={isCashback} filters={filters} />

      <ReportFilters
        ref={filtersRef}
        open={openFilters}
        onClose={() => setOpenFilters(false)}
        dateFilters={dateFilters}
        ignoreBlockOrigins
        onUpdateFilters={(filters) => setFilters(filters)}
        onUpdateDateFilters={(filters) => {
          setDateFilters(filters);
        }}
      />
    </>
  );
}
